import type { IUser } from '../interfaces/IUser';
import { ApiClientService } from './ApiClientService';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  removeStorageKeys,
  setStorageValues,
} from './storage.service';

type AuthResponse = {
  token?: {
    accessToken?: string;
    refreshToken?: string;
  };
  user?: IUser;
};

type UserResponse = {
  user: IUser;
};

export type UserNotifications = NonNullable<IUser['notifications']>;

export const getMe = async (): Promise<IUser | null> => {
  const response = await ApiClientService.getJson<{ user?: IUser }>(
    `${ApiClientService.getApiBaseUrl()}/users/checkAuthenticated`
  );

  return response.user || null;
};

export const authenticate = async (
  email: string,
  password: string
): Promise<{
  token: { accessToken: string; refreshToken: string };
  user: IUser;
}> => {
  const response = await ApiClientService.postJson<AuthResponse>(
    `${ApiClientService.getApiBaseUrl()}/users/authenticate`,
    {
      username: email,
      password,
    }
  );

  const accessToken = response?.token?.accessToken?.trim() || '';
  const refreshToken = response?.token?.refreshToken?.trim() || '';
  const user = response?.user || null;

  if (!accessToken || !refreshToken || !user) {
    throw new Error('Authentication response was incomplete.');
  }

  return {
    token: { accessToken, refreshToken },
    user,
  };
};

export const authenticateGoogle = async (
  credential: string
): Promise<{
  token: { accessToken: string; refreshToken: string };
  user: IUser;
}> => {
  const response = await ApiClientService.postJson<AuthResponse>(
    `${ApiClientService.getApiBaseUrl()}/users/authenticateGoogle`,
    {
      token: credential,
    }
  );

  const accessToken = response?.token?.accessToken?.trim() || '';
  const refreshToken = response?.token?.refreshToken?.trim() || '';
  const user = response?.user || null;

  if (!accessToken || !refreshToken || !user) {
    throw new Error('Google authentication response was incomplete.');
  }

  return {
    token: { accessToken, refreshToken },
    user,
  };
};

export const authenticateMicrosoft = async (
  code: string,
  redirectUri: string
): Promise<{
  token: { accessToken: string; refreshToken: string };
  user: IUser;
}> => {
  const response = await ApiClientService.postJson<AuthResponse>(
    `${ApiClientService.getApiBaseUrl()}/users/authenticateMicrosoft`,
    {
      code,
      redirectUri,
    }
  );

  const accessToken = response?.token?.accessToken?.trim() || '';
  const refreshToken = response?.token?.refreshToken?.trim() || '';
  const user = response?.user || null;

  if (!accessToken || !refreshToken || !user) {
    throw new Error('Microsoft authentication response was incomplete.');
  }

  return {
    token: { accessToken, refreshToken },
    user,
  };
};

export const createUser = async (name: string, email: string, password: string) => {
  await ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users`, {
    name,
    email,
    password,
  });
};

export const requestEmailVerification = async () => {
  return ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users/email-verification/request`, {});
};

export const verifyEmail = async (token: string) => {
  return ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users/email-verification/verify`, { token });
};

export const refreshToken = async (token: string) => {
  const response = await ApiClientService.postJson<AuthResponse>(
    `${ApiClientService.getApiBaseUrl()}/users/refreshToken`,
    {
      token,
    }
  );

  const accessToken = response?.token?.accessToken?.trim() || '';
  const refreshTokenValue = response?.token?.refreshToken?.trim() || '';

  if (!accessToken || !refreshTokenValue) {
    throw new Error('Refresh token response was incomplete.');
  }

  await setStorageValues({
    [ACCESS_TOKEN_KEY]: accessToken,
    [REFRESH_TOKEN_KEY]: refreshTokenValue,
  });
};

export const storeTokens = async (
  token: { accessToken: string; refreshToken: string },
  _user: IUser
): Promise<'home'> => {
  await setStorageValues({
    [ACCESS_TOKEN_KEY]: token.accessToken,
    [REFRESH_TOKEN_KEY]: token.refreshToken,
  });

  return 'home';
};

export const clearTokens = async () => {
  await removeStorageKeys([ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY]);
};

export const updateUser = async (name: string, email: string, password: string | null) => {
  return ApiClientService.putJson<UserResponse>(`${ApiClientService.getApiBaseUrl()}/users`, {
    name,
    email,
    password,
  });
};

export const updateUserNotifications = async (notifications: UserNotifications) => {
  return ApiClientService.putJson<UserResponse>(`${ApiClientService.getApiBaseUrl()}/users`, {
    notifications,
  });
};

export const createQuickSortExampleGame = async () => {
  return ApiClientService.postJson(
    `${ApiClientService.getApiBaseUrl()}/users/examples/quick-sort`,
    {}
  );
};

export const deleteUser = async () => {
  return ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/users`);
};

export const updateBillingMonthly = async (token: string) => {
  return ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users/billing/month`, {
    token,
  });
};

export const updateBillingSixMonth = async (token: string) => {
  return ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users/billing/sixmonth`, {
    token,
  });
};

export const updateBillingOrg = async (token: string, domain: string) => {
  return ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/users/billing/org`, {
    token,
    domain,
  });
};

export const updatePayment = async (token: string, accountLevel: number) => {
  return ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/users/billing`, {
    token,
    accountLevel,
  });
};

export const deleteBilling = async () => {
  return ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/users/billing`);
};
